import { useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import commonActions from "../../../Actions/commonActions";
import discountAgreementActions from "../../../Actions/Voss/discountAgreementActions";
import packageActions from "../../../Actions/Voss/packageActions";
import Card from "../../../Components/Card/Card";
import TabsCard from "../../../Components/Card/TabsCard";
import routes from "../../../Constants/routes";
import { VossApplicationScopes } from "../../../Constants/VOSS/vossApplicationScope";
import { VossDiscountAutomaticApplicationKind } from "../../../Constants/VOSS/vossDiscountAutomaticApplicationKind";
import { VossPeriodKind } from "../../../Constants/VOSS/vossPeriodKind";
import DiscountCustomerFormContainer from "./DiscountCustomerFormContainer";
import DiscountInfoFormContainer from "./DiscountInfoFormContainer";
import DiscountPricingFormContainer from "./DiscountPricingFormContainer";

function NewDiscountAgreementFormContainer({ ...props }) {
    const { packageActions, commonActions, form, discountAgreementActions, selectedPackage } = props;
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const packageId = searchParams.get("PackageId");

    useEffect(() => {

        if (packageId) {
            packageActions.getPackage(packageId);
        }

        commonActions.startFormEditing("newDiscountAgreementForm", {
            packageId: packageId,
            validFrom: new Date(),
            applicationScope: VossApplicationScopes.Any,
            periodKind: VossPeriodKind.AlignedToBindingPeriod,
            automaticApplicationKind: VossDiscountAutomaticApplicationKind.Never,
            periodIterationCount: 1,
        });

        return () => {
            packageActions.clear('package');
            commonActions.cancelFormEditing('newDiscountAgreementForm');
        };
    }, []);

    useEffect(() => {
        packageActions.getPackages();
    }, []);

    useEffect(() => {
        if (selectedPackage) {
            onChange([], 'chargePlanDiscounts');
        }
    }, [selectedPackage]);

    const navigateToEdit = (discount) => {
        const params = new URLSearchParams();
        params.append("DiscountId", discount.id);
        params.append("VersionId", discount.versionId);
        navigate({
            pathname: routes.editDiscount,
            search: params.toString()
        });
    }

    const onSave = () => {
        discountAgreementActions.createDiscountAgreement(form, navigateToEdit);
    };

    const onChange = (value, field) => {
        commonActions.changeMultiFormField('newDiscountAgreementForm', field, value);
    };

    const onAddDiscount = (discount) => {
        const chargePlanId = discount.selectedChargePlan.id;
        const productGroupId = discount.selectedProductGroupPricing;
        const productId = discount.selectedProductPricing;

        const productPricingDiscount = {
            productId,
            discountType: discount.selectedDiscountType,
            unitPriceDiscount: discount.unitPrice,
            tierPricingDiscounts: discount.tierPricingDiscounts
        };

        const existingDiscounts = form.data.chargePlanDiscounts || [];

        const chargePlanIndex = existingDiscounts.findIndex(d => d.chargePlanId === chargePlanId);

        if (chargePlanIndex !== -1) {
            const chargePlanDiscount = existingDiscounts[chargePlanIndex];

            const productGroupIndex = chargePlanDiscount.productGroupDiscounts.findIndex(g => g.productGroupId === productGroupId);

            if (productGroupIndex !== -1) {
                const productGroupDiscount = chargePlanDiscount.productGroupDiscounts[productGroupIndex];

                const productPricingIndex = productGroupDiscount.productPricingDiscounts.findIndex(p => p.productId === productId);

                if (productPricingIndex !== -1) {
                    productGroupDiscount.productPricingDiscounts[productPricingIndex] = productPricingDiscount;
                } else {
                    productGroupDiscount.productPricingDiscounts.push(productPricingDiscount);
                }
            } else {
                chargePlanDiscount.productGroupDiscounts.push({
                    productGroupId,
                    productPricingDiscounts: [productPricingDiscount],
                });
            }
        } else {
            existingDiscounts.push({
                chargePlanId,
                productGroupDiscounts: [{
                    productGroupId,
                    productPricingDiscounts: [productPricingDiscount],
                }],
            });
        }

        onChange(existingDiscounts, 'chargePlanDiscounts');
    };

    const onDeleteChargePlanDiscount = (chargePlan) => {
        const existingDiscounts = form.data.chargePlanDiscounts || [];

        const chargePlanId = chargePlan.chargePlanId;
        const chargePlanIndex = existingDiscounts.findIndex(d => d.chargePlanId === chargePlanId);

        if (chargePlanIndex !== -1) {
            existingDiscounts.splice(chargePlanIndex, 1);
        }

        onChange(existingDiscounts, 'chargePlanDiscounts');
    }

    const onDeleteProductPricingDiscount = (chargePlan, productGroup, product) => {
        const existingDiscounts = form.data.chargePlanDiscounts || [];

        const chargePlanId = chargePlan.chargePlanId;
        const productGroupId = productGroup.productGroupId;
        const productId = product.productId;

        const chargePlanIndex = existingDiscounts.findIndex(d => d.chargePlanId === chargePlanId);

        if (chargePlanIndex !== -1) {
            const chargePlanDiscount = existingDiscounts[chargePlanIndex];

            const productGroupIndex = chargePlanDiscount.productGroupDiscounts.findIndex(g => g.productGroupId === productGroupId);

            if (productGroupIndex !== -1) {
                const productGroupDiscount = chargePlanDiscount.productGroupDiscounts[productGroupIndex];

                const productPricingIndex = productGroupDiscount.productPricingDiscounts.findIndex(p => p.productId === productId);

                if (productPricingIndex !== -1) {
                    productGroupDiscount.productPricingDiscounts.splice(productPricingIndex, 1);
                }
            }
        }

        onChange(existingDiscounts, 'chargePlanDiscounts');
    };

    const tabs = [
        {
            name: 'discount-information',
            label: 'Discount information',
            id: 'discount-information',
            content: <DiscountInfoFormContainer onChange={onChange} {...props} />
        },
        {
            name: 'customer-categories',
            label: 'Customer categories',
            id: 'customer-categories',
            content: <DiscountCustomerFormContainer onChange={onChange} {...props} />
        }
    ];

    if (!form) return null;

    return (
        <div>
            <h2>Create discount agreement</h2>
            <fieldset className="d-grid gap-24 mb-0">
                <TabsCard>
                    {tabs.map((tab, idx) => (
                        <TabsCard.Tab
                            key={tab.id || tab.name || idx}
                            label={tab.label}
                            disabled={tab.disabled}
                        >
                            <TabsCard.TabBody>
                                {tab.content}
                            </TabsCard.TabBody>
                            <TabsCard.TabFooter>
                                <Card.Footer.Buttons>
                                    <button className="btn btn-sm" onClick={() => navigate(routes.discounts)}>Cancel</button>
                                    <button className="btn btn-primary btn-sm active" onClick={onSave}>Save</button>
                                </Card.Footer.Buttons>
                            </TabsCard.TabFooter>
                        </TabsCard.Tab>
                    ))}
                </TabsCard>
                <div>
                    <Card>
                        <Card.Content>
                            <DiscountPricingFormContainer
                                onAddDiscount={onAddDiscount}
                                onDeleteChargePlanDiscount={onDeleteChargePlanDiscount}
                                onDeleteProductPricingDiscount={onDeleteProductPricingDiscount}
                                chargePlanDiscounts={form.data.chargePlanDiscounts}
                                onChange={onChange}
                                {...props}
                            />
                        </Card.Content>
                    </Card>
                </div>
            </fieldset>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        packages: state.PackageReducer.packages,
        selectedPackage: state.PackageReducer.package,
        form: state.CommonReducer.forms && state.CommonReducer.forms.newDiscountAgreementForm,
        customerSearchResults: state.CommonReducer.vossCustomerSearchResults && state.CommonReducer.vossCustomerSearchResults.results,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        packageActions: bindActionCreators(packageActions, dispatch),
        discountAgreementActions: bindActionCreators(discountAgreementActions, dispatch),
        commonActions: bindActionCreators(commonActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(NewDiscountAgreementFormContainer);